import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api";
import {
  addTemplateUrl,
  getOrgTemplatesUrl,
  getTemplatesUrl,
  putOrgTemplatesUrl,
  updateTemplateUrl,
} from "../../config";
import { Status } from "../../common/constants";
import {
  IAddTemplatePayload,
  ITemplate,
  IUpdateTemplatePayload,
  initialTemplatesState,
} from "../../common/types/templateTypes";
import { RootState } from "../../store";

const getErrorMessage = (error: any, fallbackMessage: string): string => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
};

export const normalizeTemplate = (template: any): ITemplate => {
  const templateId = Number(
    template?.templateId ?? template?.id ?? template?._id,
  );
  return {
    templateId,
    name: template?.name || `Template ${templateId}`,
    imageUrl: template?.imageUrl || "",
    public: !!template?.public,
    metadata: template?.metadata || {},
    _id: template?._id,
  };
};

export const extractTemplates = (payload: any): ITemplate[] => {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.templates)
        ? payload.templates
        : Array.isArray(payload?.message)
          ? payload.message
          : [];
  return list.map(normalizeTemplate).filter((t) => !Number.isNaN(t.templateId));
};

export const mergeOrgTemplates = (
  publicTemplates: ITemplate[],
  orgTemplates: ITemplate[],
): ITemplate[] => {
  const map = new Map<number, ITemplate>();
  publicTemplates.forEach((template) => {
    map.set(template.templateId, template);
  });
  orgTemplates.forEach((template) => {
    map.set(template.templateId, template);
  });
  return Array.from(map.values()).sort(
    (a, b) => a.templateId - b.templateId,
  );
};

export const getTemplates = createAsyncThunk<
  ITemplate[],
  { public?: boolean } | void,
  { rejectValue: string }
>("templates/getTemplates", async (params, { rejectWithValue }) => {
  try {
    const query =
      params && params.public !== undefined ? `?public=${params.public}` : "";
    const response = await api.get(`${getTemplatesUrl}${query}`);
    return extractTemplates(response.data);
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to load templates right now."),
    );
  }
});

export const getOrgTemplates = createAsyncThunk<
  ITemplate[],
  string,
  { rejectValue: string }
>("templates/getOrgTemplates", async (orgId, { rejectWithValue }) => {
  try {
    const response = await api.get(`${getOrgTemplatesUrl}?orgId=${orgId}`);
    return extractTemplates(response.data);
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to load organization templates."),
    );
  }
});

export const addTemplate = createAsyncThunk<
  ITemplate | null,
  IAddTemplatePayload,
  { rejectValue: string }
>("templates/addTemplate", async (payload, { rejectWithValue }) => {
  try {
    if (!payload.file) {
      return rejectWithValue("Template image (file) is required");
    }
    const form = new FormData();
    form.append("file", payload.file);
    form.append("name", payload.name);
    form.append("public", String(!!payload.public));
    if (payload.templateId) {
      form.append("templateId", String(payload.templateId));
    }
    form.append("templateReqHeading", String(!!payload.templateReqHeading));
    form.append("templateReqMessage", String(!!payload.templateReqMessage));
    form.append("templateReqIcon", String(!!payload.templateReqIcon));
    form.append("templateReqLogo", String(!!payload.templateReqLogo));
    form.append("templateReqZipCode", String(!!payload.templateReqZipCode));
    form.append("firmwareVersion", payload.firmwareVersion ?? "");
    form.append(
      "maxAlertMessageCharacter",
      String(payload.maxAlertMessageCharacter ?? 0),
    );
    form.append(
      "maxAlertTitleCharacter",
      String(payload.maxAlertTitleCharacter ?? 0),
    );
    form.append(
      "maxAlertMessageLine",
      String(payload.maxAlertMessageLine ?? 0),
    );

    const response = await api.post(addTemplateUrl, form);
    const template = response.data?.template || response.data;
    return template ? normalizeTemplate(template) : null;
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to add template. Please try again."),
    );
  }
});

export const updateTemplate = createAsyncThunk<
  ITemplate | null,
  IUpdateTemplatePayload,
  { rejectValue: string }
>("templates/updateTemplate", async (payload, { rejectWithValue }) => {
  try {
    const form = new FormData();
    form.append("templateId", String(payload.templateId));
    form.append("name", payload.name);
    form.append("public", String(!!payload.public));
    if (payload.file) {
      form.append("file", payload.file);
    }
    form.append("templateReqHeading", String(!!payload.templateReqHeading));
    form.append("templateReqMessage", String(!!payload.templateReqMessage));
    form.append("templateReqIcon", String(!!payload.templateReqIcon));
    form.append("templateReqLogo", String(!!payload.templateReqLogo));
    form.append("templateReqZipCode", String(!!payload.templateReqZipCode));
    form.append("firmwareVersion", payload.firmwareVersion ?? "");
    form.append(
      "maxAlertMessageCharacter",
      String(payload.maxAlertMessageCharacter ?? 0),
    );
    form.append(
      "maxAlertTitleCharacter",
      String(payload.maxAlertTitleCharacter ?? 0),
    );
    form.append(
      "maxAlertMessageLine",
      String(payload.maxAlertMessageLine ?? 0),
    );

    const response = await api.put(updateTemplateUrl, form);
    const template = response.data?.template || response.data;
    return template ? normalizeTemplate(template) : null;
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to update template. Please try again."),
    );
  }
});

export const assignOrgTemplates = createAsyncThunk<
  number[],
  { orgId: string; templates: number[] },
  { rejectValue: string }
>("templates/assignOrgTemplates", async (payload, { rejectWithValue }) => {
  try {
    await api.put(`${putOrgTemplatesUrl}?orgId=${payload.orgId}`, {
      templates: payload.templates,
    });
    return payload.templates;
  } catch (error: any) {
    return rejectWithValue(
      getErrorMessage(error, "Unable to save template assignments."),
    );
  }
});

const templatesSlice = createSlice({
  name: "templates",
  initialState: initialTemplatesState,
  reducers: {
    clearTemplatesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTemplates.pending, (state) => {
        state.inFlight += 1;
        state.status = Status.PENDING;
        state.error = null;
      })
      .addCase(getTemplates.fulfilled, (state, action) => {
        state.inFlight = Math.max(0, state.inFlight - 1);
        state.status = state.inFlight > 0 ? Status.PENDING : Status.SUCCESS;
        state.templates = action.payload;
      })
      .addCase(getTemplates.rejected, (state, action) => {
        state.inFlight = Math.max(0, state.inFlight - 1);
        state.status = state.inFlight > 0 ? Status.PENDING : Status.FAILED;
        state.error = action.payload || "Unable to load templates.";
      })
      .addCase(getOrgTemplates.pending, (state) => {
        state.inFlight += 1;
        state.status = Status.PENDING;
        state.error = null;
      })
      .addCase(getOrgTemplates.fulfilled, (state, action) => {
        state.inFlight = Math.max(0, state.inFlight - 1);
        state.status = state.inFlight > 0 ? Status.PENDING : Status.SUCCESS;
        state.orgTemplates = action.payload;
      })
      .addCase(getOrgTemplates.rejected, (state, action) => {
        state.inFlight = Math.max(0, state.inFlight - 1);
        state.status = state.inFlight > 0 ? Status.PENDING : Status.FAILED;
        state.error = action.payload || "Unable to load organization templates.";
      })
      .addCase(addTemplate.pending, (state) => {
        state.addStatus = Status.PENDING;
        state.error = null;
      })
      .addCase(addTemplate.fulfilled, (state) => {
        state.addStatus = Status.SUCCESS;
      })
      .addCase(addTemplate.rejected, (state, action) => {
        state.addStatus = Status.FAILED;
        state.error = action.payload || "Unable to add template.";
      })
      .addCase(updateTemplate.pending, (state) => {
        state.updateStatus = Status.PENDING;
        state.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.updateStatus = Status.SUCCESS;
        if (action.payload) {
          state.templates = state.templates.map((template) =>
            template.templateId === action.payload?.templateId
              ? action.payload
              : template,
          );
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.updateStatus = Status.FAILED;
        state.error = action.payload || "Unable to update template.";
      })
      .addCase(assignOrgTemplates.pending, (state) => {
        state.assignStatus = Status.PENDING;
        state.error = null;
      })
      .addCase(assignOrgTemplates.fulfilled, (state) => {
        state.assignStatus = Status.SUCCESS;
      })
      .addCase(assignOrgTemplates.rejected, (state, action) => {
        state.assignStatus = Status.FAILED;
        state.error = action.payload || "Unable to save template assignments.";
      });
  },
});

export const { clearTemplatesError } = templatesSlice.actions;
export const selector = (state: RootState) => state.templates;
export default templatesSlice.reducer;
